import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { api } from "../../lib/api";
import { useSEO } from "../../lib/seo";

export default function IntegrationMatrix() {
  const [software, setSoftware] = useState([]);

  useSEO({
    title: "Pool Software Integration Matrix",
    description: "Which pool service software integrates with QuickBooks, Stripe, Zapier, SMS and more? Full side-by-side integration matrix.",
  });

  useEffect(() => {
    api.get("/software").then(({ data }) => setSoftware(data));
  }, []);

  const integrations = useMemo(() => {
    const set = new Set();
    software.forEach((s) => (s.integrations || []).forEach((i) => set.add(i)));
    return [...set].sort();
  }, [software]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="integration-matrix-page">
      <nav className="text-xs text-slate-400 mb-6"><Link to="/tools" className="hover:text-sky-600">Tools</Link> / Integration Matrix</nav>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Integration Matrix</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">Which platforms connect to the tools you already run your business on. Green check = native or official integration.</p>

      <div className="mt-12 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm min-w-[720px]" data-testid="integration-table">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="text-left px-5 py-4 font-heading font-semibold sticky left-0 bg-slate-900">Integration</th>
              {software.map((s) => (
                <th key={s.slug} className="px-4 py-4 font-heading font-semibold text-center whitespace-nowrap">
                  <Link to={`/software/${s.slug}`} className="hover:text-sky-400">{s.name}</Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {integrations.map((integ, idx) => (
              <tr key={integ} className={idx % 2 ? "bg-slate-50" : "bg-white"}>
                <td className="px-5 py-3.5 font-semibold text-slate-800 sticky left-0 bg-inherit whitespace-nowrap">{integ}</td>
                {software.map((s) => {
                  const has = (s.integrations || []).some((i) => i.toLowerCase().includes(integ.toLowerCase().split(" ")[0]) || integ.toLowerCase().includes(i.toLowerCase().split(" ")[0]));
                  const exact = (s.integrations || []).includes(integ);
                  return (
                    <td key={s.slug} className="px-4 py-3.5 text-center">
                      {exact || has ? (
                        <Check className="w-5 h-5 text-emerald-500 inline" aria-label="Supported" />
                      ) : (
                        <Minus className="w-4 h-4 text-slate-300 inline" aria-label="Not supported" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-6 text-xs text-slate-400">Data compiled from vendor documentation, June 2026. Always confirm integrations on a live demo before purchasing.</p>
    </div>
  );
}
